"use client";

import { c as _c } from "react/compiler-runtime";
import { Combobox } from "@headlessui/react";
import { useContext, useEffect, useState } from "react";
import { CommunityMemberMinimal } from "@/entities/IProfile";
import { useDebounce } from "react-use";
import { useQuery } from "@tanstack/react-query";
import { commonApiFetch } from "@/services/api/common-api";
import { ApiProfileProxy } from "@/generated/models/ApiProfileProxy";
import { AuthContext } from "@/components/auth/Auth";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
const MIN_SEARCH_LENGTH = 3;
function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}
export default function ProxyCreateTargetSearch(t0) {
  const $ = _c(30);
  const {
    profileProxy,
    loading,
    onTargetSelect
  } = t0;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  let t1;
  if ($[0] !== query) {
    t1 = () => {
      setDebouncedQuery(query);
    };
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== query) {
    t2 = [query];
    $[2] = query;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useDebounce(t1, 200, t2);
  let t3;
  let t4;
  if ($[4] !== debouncedQuery) {
    t3 = [QueryKey.PROFILE_SEARCH, {
      param: debouncedQuery,
      only_profile_owners: "true"
    }];
    t4 = async () => await commonApiFetch({
      endpoint: "community-members",
      params: {
        param: debouncedQuery ?? "",
        only_profile_owners: "true"
      }
    });
    $[4] = debouncedQuery;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  const t5 = !!debouncedQuery && debouncedQuery.length >= MIN_SEARCH_LENGTH;
  let t6;
  if ($[7] !== t3 || $[8] !== t4 || $[9] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5
    };
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    data: profiles
  } = useQuery(t6);
  let t7;
  if ($[11] !== connectedProfile || $[12] !== profiles) {
    t7 = () => {
      if (!profiles?.length) {
        return [];
      }
      if (!connectedProfile) {
        return [];
      }
      return profiles.filter(profile => profile.handle !== connectedProfile.handle);
    };
    $[11] = connectedProfile;
    $[12] = profiles;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const getFilteredProfiles = t7;
  let t8;
  if ($[14] !== getFilteredProfiles) {
    t8 = getFilteredProfiles();
    $[14] = getFilteredProfiles;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const [filteredProfiles, setFilteredProfiles] = useState(t8);
  let t9;
  if ($[16] !== getFilteredProfiles) {
    t9 = () => setFilteredProfiles(getFilteredProfiles());
    $[16] = getFilteredProfiles;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== connectedProfile || $[19] !== profiles) {
    t10 = [profiles, connectedProfile];
    $[18] = connectedProfile;
    $[19] = profiles;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  useEffect(t9, t10);
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Combobox.Label className="mb-0 tw-text-lg tw-font-semibold tw-text-iron-50">Assigned to</Combobox.Label>;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== filteredProfiles || $[23] !== loading || $[24] !== onTargetSelect || $[25] !== profileProxy) {
    t12 = profileProxy ? <div className="tw-mt-3"><div className="tw-flex tw-items-center tw-gap-x-3">{profileProxy.granted_to.pfp ? <img src={profileProxy.granted_to.pfp} alt="Profile picture" className="tw-flex-shrink-0 tw-h-6 tw-w-6 tw-flex-none tw-rounded-lg tw-bg-iron-800 tw-ring-1 tw-ring-white/30" /> : <div className="tw-flex-shrink-0 tw-h-6 tw-w-6 tw-flex-none tw-rounded-lg tw-bg-iron-800 tw-ring-1 tw-ring-white/30" />}<span className="tw-font-semibold tw-text-iron-50 tw-text-base">{profileProxy.granted_to.handle}</span><button type="button" aria-label="Remove selected profile" onClick={() => onTargetSelect(null)} className="tw-bg-transparent tw-h-8 tw-w-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border-0 focus:tw-outline-none hover:tw-bg-iron-800 tw-transition tw-duration-300 tw-ease-out"><svg className="tw-flex-shrink-0 tw-h-5 tw-w-5 tw-text-red" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg></button></div></div> : <div className="tw-relative tw-mt-4 tw-max-w-xs"><Combobox.Input className="tw-form-input tw-block tw-w-80 tw-rounded-lg tw-border-0 tw-py-3 tw-pl-11 tw-pr-4 tw-bg-iron-900 tw-text-iron-50 tw-font-normal tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 hover:tw-ring-iron-600 placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-bg-iron-900 active:tw-fill-none focus:tw-ring-1 focus:tw-ring-inset  focus:tw-ring-primary-400 tw-text-base sm:text-sm tw-transition tw-duration-300 tw-ease-out" placeholder="Search profile" autoComplete="off" onChange={event => setQuery(event.target.value)} displayValue={_temp} /><svg className="tw-pointer-events-none tw-absolute tw-left-4 tw-top-3.5 tw-h-5 tw-w-5 tw-text-iron-300" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" /></svg>{!!filteredProfiles.length && <Combobox.Options className="tw-list-none tw-px-2 tw-absolute tw-z-10 tw-mt-1 tw-max-h-56 tw-w-full tw-overflow-auto tw-rounded-md tw-bg-iron-800 tw-py-1 tw-text-base tw-shadow-lg tw-ring-1 tw-ring-white/10 tw-ring-opacity-5 focus:tw-outline-none sm:tw-text-sm">{filteredProfiles.map(profile_0 => <Combobox.Option key={profile_0.handle} value={profile_0} disabled={loading} className={_temp2}>{t13 => {
            const {
              selected
            } = t13;
            return <div className="tw-flex tw-items-center">{profile_0.pfp ? <img src={profile_0.pfp} alt="Profile picture" className="tw-h-6 tw-w-6 tw-flex-shrink-0 tw-rounded-full" /> : <div className="tw-flex-shrink-0 tw-h-6 tw-w-6 tw-flex-none tw-rounded-lg tw-bg-iron-800 tw-ring-1 tw-ring-white/30" />}<span className={classNames("tw-ml-3 tw-truncate", selected && "tw-font-semibold")}>{profile_0.handle}</span></div>;
          }}</Combobox.Option>)}</Combobox.Options>}</div>;
    $[22] = filteredProfiles;
    $[23] = loading;
    $[24] = onTargetSelect;
    $[25] = profileProxy;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== onTargetSelect || $[28] !== t12) {
    t13 = <Combobox as="div" value={null} onChange={onTargetSelect}>{t11}{t12}</Combobox>;
    $[27] = onTargetSelect;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
function _temp2(t0) {
  const {
    active
  } = t0;
  return classNames("tw-relative tw-cursor-pointer tw-select-none tw-rounded-lg tw-py-2 tw-px-2 tw-transition tw-duration-300 tw-ease-out", active ? "tw-bg-iron-700 tw-text-iron-50 tw-font-medium" : "");
}
function _temp(person) {
  return person?.handle;
}